import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { exchangeAuthCode, getTwitterCallbackUrl } from '../../services/authService';
import { getCreatorOnboardingToken, removeCreatorOnboardingToken, removeCreatorToken, removeOAuthStartedAt } from '../../services/sessionStorage';

const OAUTH_EXCHANGE_ERROR_COPY = {
  AUTH_CODE_INVALID: 'Login expired. Please start again from this device.',
  INVALID_AUTH_CODE: 'Login expired. Please start again from this device.',
  RATE_LIMITED: 'Too many login attempts. Please wait a few minutes and try again.',
  SERVER_ERROR: 'The login server had a problem. Please try again.'
};

export default function OAuthCallback({ provider }) {
  const [searchParams] = useSearchParams();
  const { login, startOnboarding } = useAuth();
  const { invalidate } = useData();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const token = searchParams.get('token');
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const err = searchParams.get('error');
    const recoveringDiscord = ['discord', 'discord-login'].includes(searchParams.get('recovered'));
    const destination = recoveringDiscord ? '/settings?connected=discord' : '/dashboard';

    if (err) {
      removeOAuthStartedAt();
      setError(decodeURIComponent(err));
      return;
    }

    if (provider === 'twitter' && code && state) {
      window.location.assign(getTwitterCallbackUrl(window.location.search));
      return;
    }

    const authenticate = async () => {
      if (recoveringDiscord) {
        removeCreatorToken();
        removeCreatorOnboardingToken();
        invalidate();
      }
      let resolvedToken = token;
      if (!resolvedToken && code) {
        const exchangeKey = `creator_oauth_exchange_${code}`;
        try {
          const existingExchange = window.sessionStorage.getItem(exchangeKey);
          const canResumeOnboarding = existingExchange === 'onboarding' && getCreatorOnboardingToken();
          if (!recoveringDiscord && (existingExchange === 'dashboard' || canResumeOnboarding)) {
            navigate(existingExchange === 'onboarding' ? '/onboarding' : '/dashboard', { replace: true });
            return;
          }
          if (existingExchange === 'onboarding' && !canResumeOnboarding) {
            window.sessionStorage.removeItem(exchangeKey);
          }
          window.sessionStorage.setItem(exchangeKey, 'started');
        } catch {
          // Some mobile browsers block sessionStorage; the ref still guards this render.
        }

        try {
          const response = await exchangeAuthCode(code);
          if (response.data?.type === 'onboarding' && response.data?.onboardingToken) {
            startOnboarding(response.data.onboardingToken);
            removeOAuthStartedAt();
            try {
              window.sessionStorage.setItem(exchangeKey, 'onboarding');
            } catch {
              // Nothing else needed; the token is stored through AuthContext.
            }
            navigate('/onboarding', { replace: true });
            return;
          }

          resolvedToken = response.data.token;
          try {
            window.sessionStorage.setItem(exchangeKey, 'dashboard');
          } catch {
            // Nothing else needed; the server also set the session cookie.
          }
        } catch (exchangeError) {
          const errorCode = exchangeError.response?.data?.code;
          removeOAuthStartedAt();
          setError(OAUTH_EXCHANGE_ERROR_COPY[errorCode] || 'Login expired. Please try again.');
          return;
        }
      }

      if (!resolvedToken) {
        removeOAuthStartedAt();
        setError('No authentication code received. Please try logging in again.');
        return;
      }

      login(resolvedToken)
        .then(() => {
          if (recoveringDiscord) invalidate();
          removeOAuthStartedAt();
          navigate(destination, { replace: true });
        })
        .catch((loginError) => {
          const status = loginError.response?.status;
          if (!recoveringDiscord && status !== 401 && status !== 403) {
            removeOAuthStartedAt();
            navigate('/dashboard', { replace: true });
            return;
          }
          removeOAuthStartedAt();
          setError('Authentication failed. Please try again.');
        });
    };

    authenticate().catch(() => {
      removeOAuthStartedAt();
      setError('Authentication failed. Please try again.');
    });
  }, []);

  if (error) {
    return (
      <div className="login-screen">
        <div className="login-box">
          <div className="login-logo">TAKEOVER</div>
          <div className="login-sub">Creator Portal</div>
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <p style={{ color: 'var(--red)', fontFamily: "'Space Mono', monospace", fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 24 }}>
              {error}
            </p>
            <a
              href="/login"
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--gold)' }}
            >
              Back to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-screen">
      <div className="login-box">
        <div className="login-logo">TAKEOVER</div>
        <div className="login-sub">Creator Portal</div>
        <div className="loading-state" style={{ minHeight: 120 }}>
          Authenticating via {provider}...
        </div>
      </div>
    </div>
  );
}
